/**
 * Keyboard Shortcut Context Provider
 *
 * Provides keyboard shortcut service to all components via React context.
 * Must be mounted in App.tsx to ensure shortcuts work globally.
 *
 * @example Basic setup in App.tsx
 * ```typescript
 * import { KeyboardShortcutProvider } from '@/services/keyboard'
 *
 * export const App = () => {
 *   return (
 *     <KeyboardShortcutProvider>
 *       <YourApp />
 *     </KeyboardShortcutProvider>
 *   )
 * }
 * ```
 */

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { KeyboardShortcutService } from './KeyboardShortcutService';

interface KeyboardShortcutContextValue {
  service: KeyboardShortcutService;
}

const KeyboardShortcutContext = createContext<KeyboardShortcutContextValue | null>(null);

export interface KeyboardShortcutProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for keyboard shortcuts
 *
 * @remarks
 * This provider initializes the global keyboard shortcut service.
 * It must be mounted high in the component tree (typically in App.tsx)
 * to ensure all shortcuts work throughout the application.
 */
export const KeyboardShortcutProvider: React.FC<KeyboardShortcutProviderProps> = ({
  children
}) => {
  const service = useMemo(() => KeyboardShortcutService.getInstance(), []);

  useEffect(() => {
    service.setEnabled(true);
    return () => {
      service.setEnabled(false);
    };
  }, [service]);

  const value = useMemo(() => ({ service }), [service]);

  return (
    <KeyboardShortcutContext.Provider value={value}>
      {children}
    </KeyboardShortcutContext.Provider>
  );
};

/**
 * Hook to access keyboard shortcut service from context
 *
 * @returns Keyboard shortcut service instance
 * @throws Error if used outside KeyboardShortcutProvider
 *
 * @example
 * ```typescript
 * const { service } = useKeyboardShortcutContext();
 * const groups = service.getShortcutGroups();
 * ```
 */
export function useKeyboardShortcutContext(): KeyboardShortcutContextValue {
  const context = useContext(KeyboardShortcutContext);

  if (!context) {
    throw new Error(
      'useKeyboardShortcutContext must be used within KeyboardShortcutProvider'
    );
  }

  return context;
}
